import React, { useState, useEffect } from "react";

/**
 * Gestileerde vervanging voor window.confirm()/window.prompt().
 *
 * Gewoon gebruik (ja/nee-bevestiging):
 *   <ConfirmDialog
 *     open={showConfirm}
 *     title="Winkel goedkeuren?"
 *     message={`Winkel "${shop.name}" goedkeuren en toegang geven?`}
 *     onConfirm={() => { ...; setShowConfirm(false); }}
 *     onCancel={() => setShowConfirm(false)}
 *   />
 *
 * Destructieve actie met naam-bevestiging (bijv. winkel verwijderen):
 *   <ConfirmDialog
 *     open={showDelete}
 *     danger
 *     title="Winkel verwijderen"
 *     message="Deze actie kan niet ongedaan worden gemaakt..."
 *     requireTypedText={shop.name}
 *     confirmLabel="Verwijderen"
 *     onConfirm={...}
 *     onCancel={...}
 *   />
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Bevestigen",
  cancelLabel = "Annuleren",
  danger = false,
  requireTypedText = null,
  onConfirm,
  onCancel,
}) {
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    if (open) setTypedText("");
  }, [open]);

  if (!open) return null;

  const confirmDisabled = requireTypedText != null && typedText.trim() !== requireTypedText.trim();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(58, 74, 104, 0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
        padding: "20px",
      }}
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmDialogTitle"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "28px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(58, 74, 104, 0.35)",
          border: "1px solid rgba(58, 74, 104, 0.08)",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            marginBottom: "16px",
            background: danger ? "rgba(179, 38, 30, 0.1)" : "rgba(58, 74, 104, 0.1)",
          }}
        >
          {danger ? "⚠️" : "❓"}
        </div>

        <h2
          id="confirmDialogTitle"
          style={{ margin: "0 0 10px 0", color: "#3A4A68", fontSize: "19px" }}
        >
          {title}
        </h2>

        <p style={{ margin: "0 0 18px 0", color: "#5b6b8c", fontSize: "14px", lineHeight: 1.5 }}>
          {message}
        </p>

        {requireTypedText != null && (
          <div style={{ marginBottom: "18px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#3A4A68",
              }}
            >
              Typ ter bevestiging: <strong>{requireTypedText}</strong>
            </label>
            <input
              type="text"
              autoFocus
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "1px solid rgba(58, 74, 104, 0.2)",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 18px",
              background: "rgba(58, 74, 104, 0.08)",
              color: "#3A4A68",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={{
              padding: "10px 18px",
              background: confirmDisabled ? "#c8ccd6" : danger ? "#B3261E" : "#3A4A68",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: confirmDisabled ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
